import { normalizeTransportEndpoint } from "../observability/transport-failures"

export const HANDLED_ERROR_KIND = "handled-api-error"

export const HANDLED_ERROR_CODES = [
  "network",
  "timeout",
  "cancelled",
  "invalid_credentials",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "validation",
  "rate_limited",
  "server",
  "malformed",
  "oauth_continuation",
  "unknown"
] as const

export type HandledErrorCode = (typeof HANDLED_ERROR_CODES)[number]

export type HandledErrorStatusClass =
  | "2xx"
  | "3xx"
  | "4xx"
  | "5xx"
  | "network"
  | "unknown"

export type HandledErrorSeverity = "error" | "warning" | "info"

export type HandledErrorActionKind = "retry" | "restart-login"

export interface HandledErrorAction {
  kind: HandledErrorActionKind
  href: string
}

export interface HandledErrorField {
  field: string
  message: string
}

export interface HandledError {
  kind: typeof HANDLED_ERROR_KIND
  code: HandledErrorCode
  messageKey: string
  severity: HandledErrorSeverity
  status: number | null
  statusClass: HandledErrorStatusClass
  endpoint: string
  method: string
  flow: string
  detail?: string
  /** The server's own code: `code` collapses every unrecognised 400 into one. */
  serverCode?: string
  fieldErrors?: HandledErrorField[]
  action?: HandledErrorAction
}

export interface HandledErrorSource {
  ok?: boolean
  status?: number | null
  problem?: string | null
  data?: unknown
  config?: { url?: string; method?: string } | null
}

export interface HandledErrorOptions {
  flow: string
  endpoint?: string
  method?: string
  code?: HandledErrorCode
  action?: HandledErrorAction
}

export const ERROR_CODE_MESSAGE_KEYS: Record<HandledErrorCode, string> = {
  network: "errors.network",
  timeout: "errors.codes.timeout",
  cancelled: "errors.codes.cancelled",
  invalid_credentials: "errors.codes.invalidCredentials",
  unauthorized: "errors.codes.unauthorized",
  forbidden: "errors.codes.forbidden",
  not_found: "errors.codes.notFound",
  conflict: "errors.codes.conflict",
  validation: "errors.codes.validation",
  rate_limited: "errors.codes.rateLimited",
  server: "errors.codes.server",
  malformed: "errors.codes.malformed",
  oauth_continuation: "errors.oauth.continuationFailed",
  unknown: "errors.unknown"
}

export const BACKEND_ERROR_CODES: Record<string, HandledErrorCode> = {
  not_authenticated: "unauthorized",
  authentication_failed: "invalid_credentials",
  token_not_valid: "unauthorized",
  permission_denied: "forbidden",
  not_found: "not_found",
  throttled: "rate_limited",
  invalid: "validation",
  parse_error: "validation",
  method_not_allowed: "unknown",
  unsupported_media_type: "validation"
}

const SEVERITY_BY_CODE: Partial<Record<HandledErrorCode, HandledErrorSeverity>> =
  {
    validation: "warning",
    conflict: "warning",
    cancelled: "info"
  }

const MAX_DETAIL_LENGTH = 240
const MAX_FIELD_ERRORS = 5
const VALIDATION_ENVELOPE_KEYS = new Set([
  "message",
  "type",
  "description",
  "data",
  "status_code",
  "code",
  "detail"
])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const isHandledError = (value: unknown): value is HandledError =>
  isRecord(value) &&
  value.kind === HANDLED_ERROR_KIND &&
  typeof value.code === "string" &&
  typeof value.messageKey === "string"

export const statusClassOf = (
  status: number | null | undefined,
  problem?: string | null
): HandledErrorStatusClass => {
  if (typeof status === "number" && status > 0) {
    if (status >= 500) return "5xx"
    if (status >= 400) return "4xx"
    if (status >= 300) return "3xx"
    if (status >= 200) return "2xx"
    return "unknown"
  }
  if (
    problem === "NETWORK_ERROR" ||
    problem === "CONNECTION_ERROR" ||
    problem === "TIMEOUT_ERROR"
  ) {
    return "network"
  }
  return "unknown"
}

const backendCodeOf = (data: unknown): HandledErrorCode | null => {
  if (!isRecord(data)) return null
  const details = isRecord(data.details) ? data.details : null
  const candidates = [data.code, details?.code, data.detail, data.error]
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate in BACKEND_ERROR_CODES) {
      return BACKEND_ERROR_CODES[candidate]
    }
  }
  return null
}

const serverCodeOf = (data: unknown): string | null => {
  if (!isRecord(data)) return null
  const details = isRecord(data.details) ? data.details : null
  const candidate = details?.code ?? data.code
  return typeof candidate === "string" ? candidate : null
}

const looksLikeValidation = (data: unknown): boolean =>
  isRecord(data) &&
  (data.message === "Validation error" ||
    (isRecord(data.details) &&
      Object.entries(data.details).some(
        ([key, value]) =>
          !VALIDATION_ENVELOPE_KEYS.has(key) &&
          Array.isArray(value) &&
          typeof value[0] === "string"
      )))

export const codeFromResponse = (source: HandledErrorSource): HandledErrorCode => {
  if (source.problem === "CANCEL_ERROR") return "cancelled"
  if (source.problem === "TIMEOUT_ERROR") return "timeout"
  if (source.problem === "NETWORK_ERROR" || source.problem === "CONNECTION_ERROR") {
    return "network"
  }

  const backendCode = backendCodeOf(source.data)
  const status = source.status ?? null

  if (status === 401) return backendCode ?? "unauthorized"
  if (status === 403) return "forbidden"
  if (status === 404) return "not_found"
  if (status === 409) return "conflict"
  if (status === 429) return "rate_limited"
  if (status === 400 || status === 422) {
    return looksLikeValidation(source.data) || backendCode === "validation"
      ? "validation"
      : (backendCode ?? "unknown")
  }
  if (status !== null && status >= 500) return "server"
  return backendCode ?? "unknown"
}

const isReadableDetail = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim().length > 0 &&
  value.length <= MAX_DETAIL_LENGTH &&
  !/[<>{}]/.test(value) &&
  !/traceback|exception|at\s+\w+\s*\(/i.test(value) &&
  !/^[a-z0-9_]+$/.test(value) &&
  !/^[a-z0-9]+(\.[a-z0-9]+)+$/i.test(value)

export const pickSafeDetail = (
  data: unknown,
  statusClass: HandledErrorStatusClass
): string | undefined => {
  if (statusClass !== "4xx" && statusClass !== "unknown") return undefined
  if (!isRecord(data)) return undefined

  const details = isRecord(data.details) ? data.details : null
  const nestedMessage = isRecord(data.message) ? data.message.detail : null
  const candidates = [
    details?.message,
    details?.description,
    data.detail,
    nestedMessage,
    data.message,
    data.error
  ]

  const readable = candidates.find(isReadableDetail)
  return readable?.trim()
}

export const pickFieldErrors = (data: unknown): HandledErrorField[] => {
  if (!isRecord(data)) return []
  const fields: HandledErrorField[] = []

  const walk = (node: Record<string, unknown>, parent: string) => {
    for (const [key, value] of Object.entries(node)) {
      if (fields.length >= MAX_FIELD_ERRORS) return
      if (VALIDATION_ENVELOPE_KEYS.has(key)) continue
      const path = parent ? `${parent}.${key}` : key

      if (Array.isArray(value) && value.length > 0) {
        const first = value[0]
        if (isReadableDetail(first)) {
          fields.push({ field: path, message: first })
        } else if (isRecord(first)) {
          walk(first, path)
        }
      } else if (isRecord(value)) {
        walk(value, path)
      }
    }
  }

  walk(isRecord(data.details) ? data.details : data, "")
  return fields
}

// Django's stock 404 text names the model it looked in, which is often not the
// thing on screen ("No Lead matches the given query." on a project page). It
// tells the user nothing the not-found title does not, so it is never shown.
const FRAMEWORK_NOT_FOUND_TEXT = /^No [\w ]+ matches the given query\.?$/i

export const isFrameworkNotFoundText = (text: string): boolean =>
  FRAMEWORK_NOT_FOUND_TEXT.test(text.trim())

export const createHandledError = (
  source: HandledErrorSource,
  options: HandledErrorOptions
): HandledError => {
  const status = typeof source.status === "number" ? source.status : null
  const statusClass = statusClassOf(status, source.problem)
  const code = options.code ?? codeFromResponse(source)
  const fieldErrors = code === "validation" ? pickFieldErrors(source.data) : []
  const detail =
    fieldErrors.length > 0 ? undefined : pickSafeDetail(source.data, statusClass)

  const error: HandledError = {
    kind: HANDLED_ERROR_KIND,
    code,
    messageKey: ERROR_CODE_MESSAGE_KEYS[code],
    severity: SEVERITY_BY_CODE[code] ?? "error",
    status,
    statusClass,
    endpoint: normalizeTransportEndpoint(
      options.endpoint ?? source.config?.url ?? null
    ),
    method: (options.method ?? source.config?.method ?? "unknown").toUpperCase(),
    flow: options.flow
  }

  const serverCode = serverCodeOf(source.data)
  if (serverCode) error.serverCode = serverCode
  if (detail && !isFrameworkNotFoundText(detail)) error.detail = detail
  if (fieldErrors.length > 0) error.fieldErrors = fieldErrors
  if (options.action) error.action = options.action

  return error
}
